import type { FormEventHandler, ReactNode } from "react";
import type { FieldErrors, UseFormRegister } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Field,
  Form,
  FormActions,
  FormDrawerFooterSlot,
  FormError,
  Input,
  Segmented,
} from "@h2j-data-team/ui";

import type { QlikAuthMethod } from "@repo/shared";

type ConnectionFormFieldsProps = {
  mode: "create" | "edit";
  authMethod: QlikAuthMethod;
  onAuthMethodChange: (method: QlikAuthMethod) => void;
  // The create and edit forms share these fields but have different value types.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  switching?: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  /** Id of the form: the actions go to the drawer's footer, outside the <form>, and submit through `form={formId}`. */
  formId: string;
  busy?: boolean;
  submitLabel: string;
  submittingLabel: string;
  /** The cancel action: a DialogClose in the drawer (so it goes through the discard guard), a button on the page. */
  cancel: ReactNode;
  submitError?: string;
};

/**
 * The connection form, for the drawers and for the pages. The actions are a
 * FormDrawerFooterSlot: pinned in the drawer's footer, in place on a page.
 */
export function ConnectionFormFields({
  mode,
  authMethod,
  onAuthMethodChange,
  register,
  errors,
  switching,
  onSubmit,
  formId,
  busy,
  submitLabel,
  submittingLabel,
  cancel,
  submitError,
}: ConnectionFormFieldsProps) {
  const { t } = useTranslation("connections");
  const showKeepEmpty = mode === "edit" && !switching;

  const fieldErrors = errors as Record<string, { message?: string } | undefined>;

  return (
    <Form id={formId} busy={busy} onSubmit={onSubmit} className="flex flex-col gap-4">
      <Field
        group
        label={t("form.authMethod")}
        hint={
          !switching && authMethod === "api_key" && mode === "create"
            ? t("form.apiKeyHint")
            : undefined
        }
      >
        <Segmented
          value={authMethod}
          onValueChange={(v) => onAuthMethodChange(v as QlikAuthMethod)}
          options={[
            { value: "api_key", label: t("form.apiKey") },
            { value: "oauth_client_credentials", label: t("form.oauth") },
          ]}
        />
      </Field>
      {switching ? <Alert tone="warning">{t("form.authMethodSwitching")}</Alert> : null}

      <Field label={t("form.name")} id="name" error={fieldErrors.name?.message}>
        <Input placeholder={t("form.namePlaceholder")} {...register("name")} />
      </Field>

      <Field label={t("form.baseUrl")} id="baseUrl" error={fieldErrors.baseUrl?.message}>
        <Input type="url" placeholder={t("form.baseUrlPlaceholder")} {...register("baseUrl")} />
      </Field>

      {authMethod === "api_key" ? (
        <Field
          id="apiKey"
          label={
            <>
              {t("form.apiKey")}
              {showKeepEmpty ? (
                <span className="ml-1 font-normal text-faint">{t("form.apiKeyKeepEmpty")}</span>
              ) : null}
            </>
          }
          error={fieldErrors.apiKey?.message}
        >
          <Input type="password" placeholder="••••••••••••••••" {...register("apiKey")} />
        </Field>
      ) : (
        <>
          <Field label={t("form.clientId")} id="clientId" error={fieldErrors.clientId?.message}>
            <Input placeholder={t("form.clientIdPlaceholder")} {...register("clientId")} />
          </Field>

          <Field
            id="clientSecret"
            label={
              <>
                {t("form.clientSecret")}
                {showKeepEmpty ? (
                  <span className="ml-1 font-normal text-faint">
                    {t("form.clientSecretKeepEmpty")}
                  </span>
                ) : null}
              </>
            }
            error={fieldErrors.clientSecret?.message}
          >
            <Input type="password" placeholder="••••••••••••••••" {...register("clientSecret")} />
          </Field>
        </>
      )}

      {submitError ? <FormError focus>{submitError}</FormError> : null}

      <FormDrawerFooterSlot>
        <FormActions
          busy={busy}
          submitLabel={submitLabel}
          submittingLabel={submittingLabel}
          submitProps={{ form: formId }}
        >
          {cancel}
        </FormActions>
      </FormDrawerFooterSlot>
    </Form>
  );
}
